/**
 * 角色管理 API
 * 对应 foundation-service (7092)
 */

import { unifiedApi } from '@/utils/unifiedApi';
import handleApiResponse from '@/utils/apiResponseHandler';
import type { Role, RoleQueryParams, RoleFormData, Permission, RoleTreeNode, RoleStatistics, InheritedPermission } from './types';

/** 后端分页参数名为 page，Ant Design 用的是 current，需转换（同 userListParams.ts） */
const toRoleListRequestParams = (params: RoleQueryParams = {}) => {
  const { current, assignmentScene, ...rest } = params;
  return {
    ...rest,
    ...(assignmentScene ? { assignment_scene: assignmentScene } : {}),
    ...(current !== undefined ? { page: current } : {}),
  };
};

/** 获取角色列表 */
export const getRoleList = async (params: RoleQueryParams = {}) => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: Role[]; count: number }>('/roles', { params: toRoleListRequestParams(params) })
  );
};

/** 获取角色详情 */
export const getRoleById = async (id: string) => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: Role; count: number }>(`/roles/${id}`)
  );
};

/** 创建角色 */
export const createRole = async (data: RoleFormData) => {
  return handleApiResponse(
    unifiedApi.service('role').post<{ data: Role; count: number }>('/roles', data)
  );
};

/** 更新角色 */
export const updateRole = async (id: string, data: Partial<RoleFormData>) => {
  return handleApiResponse(
    unifiedApi.service('role').put<{ data: Role; count: number }>(`/roles/${id}`, data)
  );
};

/** 删除角色 */
export const deleteRole = async (id: string) => {
  return handleApiResponse(
    unifiedApi.service('role').delete<{ data: null; count: number }>(`/roles/${id}`)
  );
};

/** 分配角色权限
 *
 * 后端 PUT /roles/:id/permissions 收的是 permission_ids（[]int64），此处历史上发的是
 * menu_ids，字段名对不上 → go-zero 报 `type mismatch for field "permission_ids"`
 * （必填字段缺失时的报错文案，有误导性）。
 *
 * 只写权限表 fnd_role_permissions，不碰 fnd_role_menus —— 菜单归属由 migration 维护，
 * 全站没有任何前端入口分配角色菜单，把权限 id 灌进 /roles/:id/menus 会清空菜单。
 */
export const assignRolePermissions = async (roleId: string, permissionIds: Array<string | number>) => {
  return handleApiResponse(
    unifiedApi.service('role').put<{ data: null; count: number }>(
      `/roles/${roleId}/permissions`,
      { permission_ids: permissionIds.map(Number) }
    )
  );
};

/** 获取角色权限
 *
 * 后端返回 { permissions: [...] }，不是裸数组。三个调用方（RoleList / RolePermissionTree /
 * RoleInheritanceViz）此前各自按 data / data.data / data.list 猜形状，全都取不到 → 权限
 * 设置保存后再打开不回显。在这里统一拆成数组，调用方直接拿 data 用。
 */
export const getRolePermissions = async (roleId: string) => {
  const res = await handleApiResponse(
    unifiedApi.service('role').get<{ permissions: Permission[] }>(`/roles/${roleId}/permissions`)
  );
  return { ...res, data: res.data.permissions };
};

// ==================== 角色补全 API ====================

/** 获取角色树 */
export const getRoleTree = async () => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: RoleTreeNode[]; count: number }>('/roles/tree')
  );
};

/** 获取角色统计 */
export const getRoleStatistics = async () => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: RoleStatistics; count: number }>('/roles/statistics')
  );
};

/** 获取角色继承权限 */
export const getRoleInheritedPermissions = async (roleId: string) => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: InheritedPermission[]; count: number }>(`/roles/${roleId}/inherited-permissions`)
  );
};

/** 获取子角色列表 */
export const getRoleChildren = async (roleId: string) => {
  return handleApiResponse(
    unifiedApi.service('role').get<{ data: Role[]; count: number }>(`/roles/${roleId}/children`)
  );
};

/** 角色 API 对象 */
export const roleApi = {
  getRoleList,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  assignRolePermissions,
  getRolePermissions,
  getRoleTree,
  getRoleStatistics,
  getRoleInheritedPermissions,
  getRoleChildren,
};

export default roleApi;

/** Load all assignable roles; dropdowns must not silently truncate at 100/200. */
function readRolePage(value: unknown): { list: Role[]; total?: number } {
  let current = value;
  for (let depth = 0; depth < 6; depth++) {
    if (Array.isArray(current)) return { list: current as Role[] };
    if (!current || typeof current !== 'object') break;

    const page = current as { list?: unknown; data?: unknown; total?: unknown; count?: unknown };
    if (Array.isArray(page.list)) {
      return {
        list: page.list as Role[],
        total: typeof page.total === 'number' ? page.total : typeof page.count === 'number' ? page.count : undefined,
      };
    }
    if (Array.isArray(page.data)) {
      return {
        list: page.data as Role[],
        total: typeof page.total === 'number' ? page.total : typeof page.count === 'number' ? page.count : undefined,
      };
    }
    if ('data' in page) {
      current = page.data;
      continue;
    }
    break;
  }
  throw new Error('角色列表响应格式错误');
}

export async function getAssignableRoleOptions(): Promise<Role[]> {
  const roles: Role[] = [];
  for (let page = 1; ; page++) {
    const response = await getRoleList({ current: page, pageSize: 200, assignmentScene: 'employee_assignment' });
    const { list, total } = readRolePage(response);
    const known = new Set(roles.map(role => role.id));
    const incoming = list.filter(role => !known.has(role.id));
    roles.push(...incoming);
    if ((total !== undefined && roles.length >= total) || list.length < 200) return roles;
    if (incoming.length === 0) throw new Error('角色分页未返回新数据，请重试');
  }
}
